<template>
    <HeaderContent :title="title" :is-back="true" @back="goBack" />

    <div class="mt-3">
        <!-- Card 1: General Info -->
        <SpjGeneralInfo />

        <!-- Card 2: Program -->
        <SpjProgram />

        <!-- Card 3: Fees -->
        <SpjFees />


        <!-- Card 4: Pricing -->
        <SpjPricing />

        <div class="d-flex justify-content-end mb-5">
            <BButton variant="secondary" class="me-2" @click="goBack">Cancel</BButton>
            <BButton variant="primary" @click="save">Save SPJ</BButton>
        </div>
    </div>
</template>

<script setup>
import { reactive, computed, onMounted, provide } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useAuthStore } from "@/store/auth";
import { useLoading } from "vue-loading-overlay";
import axios from "axios";
import useVuelidate from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import moment from "moment";

import HeaderContent from "@/components/HeaderContent.vue";
import SpjGeneralInfo from "./components/SpjGeneralInfo.vue";
import SpjProgram from "./components/SpjProgram.vue";
import SpjPricing from "./components/SpjPricing.vue";
import SpjFees from "./components/SpjFees.vue";

import { BButton } from "bootstrap-vue-next";

const route = useRoute();
const router = useRouter();
const toast = useToast();
const auth = useAuthStore();
const $loading = useLoading();

const isEdit = computed(() => !!route.params.uuid);
const title = computed(() => (isEdit.value ? "Edit SPJ" : "Create SPJ"));

const form = reactive({
    offer_uuid: "",
    guest_uuid: "",
    vehicle_uuid: "",
    staff_position: "Driver",
    staff_uuid: "",
    start: "",
    end: "",
    pax: 1,
    grand_total: 0,
    remarks: "",
    trans_date: moment().format("YYYY-MM-DD"),
    
    // Details
    programs: [],
    entrance_fees: [],
    parking_fees: [],
    other_fees: [],
    hotel_fees: [],
    
});

const rules = {
    // spj_no: { required: helpers.withMessage("SPJ No is required", required) },
    offer_uuid: { required: helpers.withMessage("Offer Invoice is required", required) },
    guest_uuid: { required: helpers.withMessage("Guest is required", required) },
    vehicle_uuid: { required: helpers.withMessage("Vehicle is required", required) },
    staff_uuid: { required: helpers.withMessage("Driver/Guide is required", required) },
    start: { required: helpers.withMessage("Departure Date is required", required) },
    end: { required: helpers.withMessage("Return Date is required", required) },
    pax: { required: helpers.withMessage("PAX is required", required) },
    trans_date: { required: helpers.withMessage("Transaction Date is required", required) },
};

const vSpj$ = useVuelidate(rules, form, { $scope: false });

// Provide form state to children
provide('form', form);
provide('v$', vSpj$);
provide('isEdit', isEdit);

const goBack = () => {
    router.push({ name: "spj" });
};

const save = async () => {
    const isFormCorrect = await vSpj$.value.$validate();
    // console.log(vSpj$.value.$errors);
    if (!isFormCorrect) {
        toast.error("Please fill in all required fields.");
        return;
    }

    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });

    try {
        const url = isEdit.value
            ? `${process.env.VUE_APP_API_URL}/spj/edit/${route.params.uuid}`
            : `${process.env.VUE_APP_API_URL}/spj/add`;

        const method = isEdit.value ? "put" : "post";

        const response = await axios[method](url, form, {
            headers: { Authorization: `Bearer ${auth._token}` },
        });

        if (response.data.status) {
            toast.success(response.data.message);
            goBack();
        }
    } catch (error) {
        console.error("Error saving SPJ:", error);
        toast.error(error.response?.data?.message || "Failed to save SPJ");
    } finally {
        loader.hide();
    }
};

const fetchDetail = async () => {
    if (!isEdit.value) {
        return;
    }

    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });

    try {
        const response = await axios.get(
            `${process.env.VUE_APP_API_URL}/spj/${route.params.uuid}`,
            {
                headers: { Authorization: `Bearer ${auth._token}` },
            }
        );

        if (response.data.status) {
            Object.assign(form, response.data.data);
            
            // Format dates
            if (form.start) form.start = moment(form.start).format("YYYY-MM-DD");
            if (form.end) form.end = moment(form.end).format("YYYY-MM-DD");
            if (form.trans_date) form.trans_date = moment(form.trans_date).format("YYYY-MM-DD");
        }
    } catch (error) {
        console.error("Error fetching SPJ details:", error);
        toast.error("Failed to load SPJ details");
        goBack();
    } finally {
        loader.hide();
    }
};

onMounted(() => {
    fetchDetail();
});
</script>
